import { useRef, useState } from "react"
import { Combobox } from "@base-ui/react/combobox"
import { MapPin } from "lucide-react"

import { cn } from "@/lib/utils"
import { type CityRecord, flagOf, loadCities, searchCities } from "../data/cities"
import { birthPlaceLabel, encodeBirthPlace, parseBirthPlace } from "../lib/birthplace"

interface CityComboboxProps {
  /** Stored birthplace answer (plain label or JSON with coords). */
  value: string
  onValueChange: (value: string) => void
  placeholder?: string
}

/** Rebuild a display-only record from a previously stored birthplace answer
 *  (e.g. when the user navigates back), so the input shows the label. */
function syntheticRecord(value: string): CityRecord | null {
  const parsed = parseBirthPlace(value)
  const label = parsed?.label ?? birthPlaceLabel(value)
  if (!label.trim()) return null
  const [city, ...rest] = label.split(",")
  return {
    label,
    city: city.trim(),
    country: rest.join(",").trim(),
    cc: "",
    region: "",
    id: "stored",
    ...(parsed ? { lat: parsed.lat, lon: parsed.lon } : {}),
  }
}

/**
 * City picker built on the base-ui Combobox: the user types, we filter the
 * lazily-loaded GeoNames dataset ourselves (`filter={null}`) and store the
 * chosen "City, Country" string. The popup only opens when there are matches.
 */
export function CityCombobox({ value, onValueChange, placeholder }: CityComboboxProps) {
  const [selected, setSelected] = useState<CityRecord | null>(() => syntheticRecord(value))
  const [query, setQuery] = useState(() => birthPlaceLabel(value))
  const [items, setItems] = useState<CityRecord[]>([])
  const [open, setOpen] = useState(false)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const handleInput = (input: string, reason: Combobox.Root.ChangeEventDetails["reason"]) => {
    // On close without a selection base-ui rewrites the input to the selected
    // value (i.e. blanks it). Ignore that so a typo like "New You" keeps its text.
    if (reason === "input-clear") return
    setQuery(input)
    if (timer.current) clearTimeout(timer.current)

    // Only the user's own typing starts a search. base-ui also rewrites the input
    // when an item is picked ("item-press") or arrowed to, and searching for the
    // resulting "City, Country" matches nothing — which popped the list back open
    // on "No places found" immediately after a successful choice.
    if (reason !== "input-change") return

    if (input.trim() === "") {
      setItems([])
      setOpen(false)
      if (selected) {
        setSelected(null)
        onValueChange("")
      }
      return
    }
    timer.current = setTimeout(() => {
      loadCities().then((rows) => {
        setItems(searchCities(rows, input))
        setOpen(true)
      })
    }, 200)
  }

  const handleSelect = (item: CityRecord | null) => {
    setSelected(item)
    setQuery(item ? item.label : "")
    setOpen(false)
    onValueChange(item ? encodeBirthPlace({ label: item.label, lat: item.lat, lon: item.lon }) : "")
  }

  return (
    <Combobox.Root
      items={items}
      filter={null}
      value={selected}
      onValueChange={handleSelect}
      inputValue={query}
      onInputValueChange={(next, details) => handleInput(next, details.reason)}
      open={open}
      onOpenChange={(next) => {
        if (!next) setOpen(false)
      }}
      itemToStringValue={(item: CityRecord) => item.label}
      isItemEqualToValue={(a: CityRecord, b: CityRecord) => a.id === b.id}
      autoHighlight
    >
      <div data-slot="city-combobox" className="relative">
        <MapPin className="pointer-events-none absolute left-3 top-1/2 size-5 -translate-y-1/2 text-purple-light" />
        <Combobox.Input
          placeholder={placeholder || "Place of birth"}
          className={cn(
            "h-12 w-full rounded-xl bg-purple-light/10 pl-10 pr-3 font-body text-base font-medium text-purple-light outline-none transition-colors placeholder:text-purple-light/40",
            "focus:ring-2 focus:ring-main-purple focus:ring-inset",
          )}
        />
      </div>

      <Combobox.Portal>
        <Combobox.Positioner sideOffset={6} align="start" className="z-50 w-[var(--anchor-width)]">
          <Combobox.Popup className="max-h-72 overflow-y-auto rounded-2xl border border-purple-light/10 bg-dark-surface p-1 font-body shadow-xl shadow-black/40">
            <Combobox.Empty className="px-3 py-2.5 text-base text-purple-light/50 empty:m-0 empty:p-0">
              No places found
            </Combobox.Empty>
            <Combobox.List>
              {(item: CityRecord) => (
                <Combobox.Item
                  key={item.id}
                  value={item}
                  className={cn(
                    "flex cursor-pointer items-center gap-3 rounded-xl px-3 py-2.5 text-purple-light",
                    "data-[highlighted]:bg-purple-light/10",
                  )}
                >
                  <span className="text-xl leading-none">{flagOf(item.cc)}</span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-base font-medium">{item.label}</span>
                    {item.region && (
                      <span className="block truncate text-sm text-purple-light/50">{item.region}</span>
                    )}
                  </span>
                </Combobox.Item>
              )}
            </Combobox.List>
          </Combobox.Popup>
        </Combobox.Positioner>
      </Combobox.Portal>
    </Combobox.Root>
  )
}
